/**
 * RecordLinksViewElement — F.RL (powiązania między kartotekami).
 *
 * Klocek palety buildera reprezentujący blok "Powiązania rekordów"
 * (`record_links_view`, patrz `RecursiveFormRenderer` PLACEHOLDER_TYPES) —
 * mirror `DocumentItemsViewElement`. Na REALNEJ stronie kartoteki/dokumentu
 * ten typ jest dispatchowany WYŁĄCZNIE przez `contextRenderers['record_links_view']`
 * (→ `RecordLinksSection`) — ten rejestr obsługuje TYLKO podgląd w canvasie
 * buildera (mode='builder'), gdzie nie ma prawdziwego rekordu do wyświetlenia.
 *
 * `allowedModules` scopinguje ten klocek do kontrahentów/pracowników + 7 modułów
 * klasy Dokumenty — jedyne miejsca, gdzie `contextRenderers['record_links_view']`
 * jest realnie wpięty (`ContractorDetail`/`EmployeeDetail`/
 * `document-additional-renderers.tsx`). Towar (`ProductDetail`) NIE używa silnika
 * layoutu (kartoteka target-only w F.RL v1) — celowo pominięty.
 *
 * `propertiesEditor` — per-placement konfiguracja (`allowAdd`/`allowUnlink`/
 * `allowedLinkTypeIds`/`allowedTargetTypes`/`defaultLinkTypeId`), montuje
 * `RecordLinksElementConfigEditor` jako komponent-dziecko (mirror
 * `DocumentItemsColumnsCanvas` (osobny plik, montowany przez `render` klocka
 * `DocumentItemsViewElement`) — hooki żyją w dziecku, nie
 * w ciele `propertiesEditor`, bo ten jest wołany jak zwykła funkcja). Kontrakt
 * settings + honorowanie w bloku → `RecordLinksSection.tsx` (docstring
 * nagłówkowy „Per-placement config").
 */

import { Link as LinkIcon } from 'lucide-react';

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import { RecordLinksElementConfigEditor } from '@/components/record-links/RecordLinksElementConfigEditor';
import i18n from '@/i18n';

const RECORD_LINKS_ALLOWED_MODULES = [
  'contractors',
  'employees',
  'purchase_invoices',
  'sale_invoices',
  'contracts',
  'letters',
  'commercial',
  'internal',
  'specjalne',
];

export const RecordLinksViewElement: LayoutElementDefinition = {
  type: 'record_links_view',
  category: 'tresc',
  isContainer: false,
  label: 'Powiązania rekordów',
  icon: 'link',
  allowedModules: RECORD_LINKS_ALLOWED_MODULES,
  defaultSettings: () => ({
    allowAdd: true,
    allowUnlink: true,
    allowedLinkTypeIds: [],
    allowedTargetTypes: [],
    defaultLinkTypeId: null,
  }),
  propertiesEditor: ({ settings, onChange }) => (
    <RecordLinksElementConfigEditor settings={settings} onChange={onChange} />
  ),
  render: ({ element }) => (
    <div
      className="rounded-lg border border-dashed border-border-secondary bg-bg-secondary p-4 text-center"
      data-testid={`record-links-view-element-${element.id}`}
      data-element-type="record_links_view"
    >
      <LinkIcon className="mx-auto h-5 w-5 text-text-muted" aria-hidden="true" />
      <p className="mt-1 text-xs font-medium text-text-secondary">
        {i18n.t('tenant.recordLinks.paletteLabel', { defaultValue: 'Powiązania rekordów' })}
      </p>
    </div>
  ),
};
